import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';

const ResetPasswordPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    if (!token) {
      toast.error('Invalid reset token. Please request a new password reset.');
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword(token, data.password);
      setIsSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      toast.error(error.message || 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="h-screen flex overflow-hidden">
        {/* Left Side - Full Screen Image (45%) */}
        <div className="w-[45%] relative overflow-hidden">
          <img
            src="/login_side_image.png"
            alt="Healthcare Professional"
            className="absolute top-0 left-0 w-full h-[115vh] object-cover"
          />
        </div>

        {/* Right Side - Success Message (55%) */}
        <div className="w-[55%] flex items-start justify-center bg-white pt-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-xs w-full space-y-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-green-600 p-3 rounded-full">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-blue-600 mb-4">
                Password Reset Successful!
              </h1>
              
              {/* Medi Fusion Logo */}
              <div className="flex justify-center mb-4">
                <img
                  src="/login_medi_logo.png"
                  alt="Medi Fusion"
                  className="h-16 w-auto"
                />
              </div>
              
              <p className="text-sm text-gray-600 mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              
              <Link
                to="/auth/login"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 inline-block text-center"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="h-screen flex overflow-hidden">
        {/* Left Side - Full Screen Image (45%) */}
        <div className="w-[45%] relative overflow-hidden">
          <img
            src="/login_side_image.png"
            alt="Healthcare Professional"
            className="absolute top-0 left-0 w-full h-[115vh] object-cover"
          />
        </div>

        {/* Right Side - Error Message (55%) */}
        <div className="w-[55%] flex items-start justify-center bg-white pt-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-xs w-full space-y-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-red-600 p-3 rounded-full">
                  <Lock className="h-8 w-8 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-blue-600 mb-4">
                Invalid Reset Link
              </h1>
              
              {/* Medi Fusion Logo */}
              <div className="flex justify-center mb-4">
                <img
                  src="/login_medi_logo.png"
                  alt="Medi Fusion"
                  className="h-16 w-auto"
                />
              </div>
              
              <p className="text-sm text-gray-600 mb-6">
                The password reset link is invalid or has expired. Please request a new one.
              </p>
              
              <div className="space-y-4">
                <Link
                  to="/auth/forgot-password"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 inline-block text-center"
                >
                  Request New Reset Link
                </Link>
                
                <div>
                  <Link
                    to="/auth/login"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Full Screen Image (45%) */}
      <div className="w-[45%] relative overflow-hidden">
        <img
          src="/login_side_image.png"
          alt="Healthcare Professional"
          className="absolute top-0 left-0 w-full h-[115vh] object-cover"
        />
      </div>

      {/* Right Side - Reset Password Form (55%) */}
      <div className="w-[55%] flex items-start justify-center bg-white pt-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xs w-full space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-blue-600 mb-4">
              Reset Your Password
            </h1>
            
            {/* Medi Fusion Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/login_medi_logo.png"
                alt="Medi Fusion"
                className="h-16 w-auto"
              />
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Enter your new password below
            </p>
          </div>

          {/* Reset Password Form */}
          <div className="space-y-6">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* New Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password:
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password:
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirm_password', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === password || 'Passwords do not match',
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirm_password.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            {/* Back to Login */}
            <div className="text-center">
              <Link
                to="/auth/login"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 